import { Badge } from '@ordo/ui/badge'
import type { InboundMessage } from 'types/inbound'

import { inboundDeliveryStatus, inboundSenderLabel } from '../../lib/inbound'

export function InboundMessageContent({ message }: { message: InboundMessage }) {
  return (
    <div className="min-w-0 space-y-5">
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">{message.attachments.length ? 'MMS' : 'SMS'}</Badge>
        <Badge variant={message.terminalAt ? 'destructive' : 'secondary'}>{inboundDeliveryStatus(message)}</Badge>
      </div>
      <dl className="grid min-w-0 grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <Detail label="Sender" value={inboundSenderLabel(message)} />
        <Detail label="Phone number" value={message.fromPhone} />
        <Detail label="Received" value={new Date(message.receivedAt).toLocaleString()} />
        <Detail label="Receiving number" value={message.toPhone} />
        <Detail label="Forward to" value={message.destinationUrl} />
        <Detail label="Receiving app" value={message.audience} />
        <Detail label="Delivery attempts" value={String(message.attempts)} />
        {message.senderStatus === 'known' && message.contact?.email ? (
          <Detail label="Contact email" value={message.contact.email} />
        ) : null}
        {message.deliveredAt ? (
          <Detail label="Delivered" value={new Date(message.deliveredAt).toLocaleString()} />
        ) : null}
      </dl>
      <section className="space-y-2">
        <h3 className="text-sm font-medium">Message</h3>
        <p className="whitespace-pre-wrap break-words rounded-md border bg-muted/40 p-3 text-sm">
          {message.body || 'No text content'}
        </p>
      </section>
      {message.attachments.length ? (
        <section className="space-y-2">
          <h3 className="text-sm font-medium">Attachments ({message.attachments.length})</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            {message.attachments.map((attachment, index) => (
              <li key={index} className="break-words rounded-md border p-3">
                Attachment {index + 1} · {attachment.contentType}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {message.filtered ? (
        <p className="rounded-md border p-3 text-sm text-muted-foreground">
          This message was retained without forwarding because it did not match one recognized contact.
        </p>
      ) : null}
      {message.lastError ? (
        <div role="alert" className="break-words rounded-md border border-destructive/30 p-3 text-sm text-destructive">
          {message.lastError}
        </div>
      ) : null}
    </div>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-words">{value}</dd>
    </div>
  )
}
